import React, { useMemo } from 'react';
import { App, Button } from 'antd';

import ItemFieldRow from './ItemFieldRow';
import { elementLabel } from './itemValueDisplay';
import { SHARE } from '../icons';
import useLinkedEntry, { entryLink } from '../../hooks/useLinkedEntry';
import copyTextToClipboard from '../../utils/clipboard';
import { entriesOf, isHeading, nounsOf, visibleColumns } from '../../utils/repeatingGroupEntries';

/**
 * A repeating group's entries on a READ surface (KPR-680): the item read view,
 * the detail pane, the inline detail on a phone, a shared page, a printed
 * sheet.
 *
 * Two shapes, chosen by the columns (the builder's call, platform-standards
 * § 4 "A repeating group"):
 *
 *   - a TABLE-LIKE LIST when the group has up to `TABLE_MAX_COLUMNS` short
 *     columns — one row per entry under column heads, the way an ingredient
 *     list or an invoice reads. On a phone the table stacks: each cell
 *     carries its column's name before its value, so nothing scrolls sideways;
 *   - one CARD per entry when it has more columns, or any long one (long
 *     text, a location, several values): label over value, like the fields
 *     of an item.
 *
 * A heading entry is a subhead in either shape. Every entry has a stable
 * anchor — `id="entry-<id>"`, the id keepr issued, which survives edits and
 * reordering — and, where the item's address is known, a button that copies
 * the link to it; an address that names one (`#entry-<id>`) scrolls to it and
 * marks it. Columns hidden from the reader are not in the list the card
 * serves, so they are not here.
 *
 * A FILE column (KPR-934; keepr-api KPR-750) is read as a file element is: each
 * file by name or picture, opened or downloaded from the item read's `files`
 * facts (`item`; `canDownload` is the reader's right to take copies). With the
 * collection's attachments off (`filesVisible` false) the column is not drawn,
 * as a file element is not; its stored files stay and come back with the switch.
 */

export const TABLE_MAX_COLUMNS = 5;

// Types that need a card's room, never a table cell's: a paragraph, a place,
// a file, and anything holding several values.
const WIDE_TYPES = new Set(['text-large', 'rich-text', 'location', 'file']);
const isWideColumn = column => WIDE_TYPES.has(column.dataType) || column.options?.allowMultiple === true;

/** Table or cards, for these columns. */
export const entriesLayoutFor = columns => (
	columns.length > 0 && columns.length <= TABLE_MAX_COLUMNS && !columns.some(isWideColumn) ? 'table' : 'cards'
);

function LinkButton({ entry, entryLinks, name }) {
	const { message } = App.useApp();
	if (!entry.id || !entryLinks?.collectionId || !entryLinks?.itemId) return null;
	const copy = async () => {
		const ok = await copyTextToClipboard(entryLink(entryLinks.collectionId, entryLinks.itemId, entry.id));
		if (ok) message.success('Link copied.'); else message.error('Could not copy the link.');
	};
	return (
		<Button
			type="text"
			size="small"
			className="entries-read__link"
			icon={<SHARE aria-hidden="true" />}
			aria-label={`Copy link to ${name}`}
			title="Copy link to this entry"
			onClick={copy}
		/>
	);
}

export default function RepeatingGroupValue({ element, value, resolveLookupValue, unitSystem = null, entryLinks = null, item = null, canDownload = true, filesVisible = true }) {
	const entries = useMemo(() => entriesOf(value), [value]);
	const columns = useMemo(() => {
		const readable = visibleColumns(element);
		return filesVisible === false ? readable.filter(column => column.dataType !== 'file') : readable;
	}, [element, filesVisible]);
	const linkedId = useLinkedEntry(entries);
	const { singular, singularLabel } = nounsOf(element);
	const layout = entriesLayoutFor(columns);

	if (!entries.length) {
		return <div className="item-pane__value" data-testid="entries-empty">—</div>;
	}

	let ordinal = 0;
	const rows = entries.map((entry, index) => {
		const heading = isHeading(entry);
		if (!heading) ordinal += 1;
		const name = heading ? `heading ${entry.section}` : `${singular} ${ordinal}`;
		return { entry, heading, ordinal, name, key: entry.id || `i${index}` };
	});

	const cellFor = (entry, column) => (
		<ItemFieldRow
			element={column}
			value={entry.elements?.[column.name]}
			resolveLookupValue={resolveLookupValue}
			unitSystem={unitSystem}
			item={item}
			canDownload={canDownload}
			bare
			facet={false}
		/>
	);

	if (layout === 'table') {
		return (
			<div className="entries-read entries-read--table" data-testid="entries-read">
				<div className="entries-read__table-wrap">
					<table className="entries-read__table">
						<thead>
							<tr>
								<th scope="col" className="entries-read__ordinal"><span className="visually-hidden">Number</span></th>
								{columns.map(column => <th scope="col" key={column.name}>{elementLabel(column)}</th>)}
								<th scope="col" className="entries-read__ordinal"><span className="visually-hidden">Link</span></th>
							</tr>
						</thead>
						<tbody>
							{rows.map(({ entry, heading, ordinal: n, name, key }) => (heading ? (
								<tr key={key} id={entry.id ? `entry-${entry.id}` : undefined} className={entry.id && entry.id === linkedId ? 'entries-read__row--linked' : undefined}>
									<th scope="row" colSpan={columns.length + 2} className="entries-read__heading-cell">{entry.section}</th>
								</tr>
							) : (
								<tr key={key} id={entry.id ? `entry-${entry.id}` : undefined} className={entry.id && entry.id === linkedId ? 'entries-read__row--linked' : undefined}>
									<td className="entries-read__ordinal">{n}</td>
									{columns.map(column => (
										<td key={column.name} data-label={elementLabel(column)}>{cellFor(entry, column)}</td>
									))}
									<td className="entries-read__ordinal"><LinkButton entry={entry} entryLinks={entryLinks} name={name} /></td>
								</tr>
							)))}
						</tbody>
					</table>
				</div>
			</div>
		);
	}

	return (
		<div className="entries-read entries-read--cards" data-testid="entries-read">
			<ol className="entries-read__list">
				{rows.map(({ entry, heading, ordinal: n, name, key }) => (heading ? (
					<li key={key} id={entry.id ? `entry-${entry.id}` : undefined} className="entries-read__heading-item">
						<h4 className="entries-read__heading">{entry.section}</h4>
					</li>
				) : (
					<li
						key={key}
						id={entry.id ? `entry-${entry.id}` : undefined}
						className={`entries-read__card${entry.id && entry.id === linkedId ? ' entries-read__card--linked' : ''}`}
					>
						<div className="entries-read__card-head">
							<span className="micro-label">{singularLabel} {n}</span>
							<LinkButton entry={entry} entryLinks={entryLinks} name={name} />
						</div>
						<div className="entries-read__card-fields">
							{columns.map(column => (
								<div
									key={column.name}
									className={`entries-read__card-field${isWideColumn(column) ? ' entries-read__card-field--full' : ''}`}
								>
									<ItemFieldRow
										element={column}
										value={entry.elements?.[column.name]}
										resolveLookupValue={resolveLookupValue}
										unitSystem={unitSystem}
										item={item}
										canDownload={canDownload}
										facet={false}
									/>
								</div>
							))}
						</div>
					</li>
				)))}
			</ol>
		</div>
	);
}
